import { ApolloClient } from '@apollo/client';

import Cart from 'src/hooks/use-cart';
import { ERNIE_TYPES } from 'shared/constants';
import {
  getDefaultRewardsProgramDisplayName,
  getDefaultRewardsProgramDisplayDescription,
} from 'shared/core/helpers/dispensaries';
import { formatBirthdayForRewards } from 'shared/helpers/rewards';
import { getLogger } from 'src/utils/datadog-logger';
import { CreateSpringbigUserDocument, GetRewardsV2Document } from 'src/gql/graphql';
import type { Dispensaries } from 'src/gql/graphql';
import type { UseLoyaltyEnrollmentAnalyticsReturn } from 'src/hooks/use-loyalty-enrollment-analytics';
import { Statuses, UseLoyaltyReturn, ShowErnie } from './types';
import { setLoyaltyData } from '../loyalty.helpers';

const logger = getLogger('loyalty');

type User = {
  id: string;
  phone: string;
  birthday: string;
};

type Dependencies = {
  dispensary: Dispensaries;
  cart: ReturnType<typeof Cart>;
  user: User;
  apolloClient: ApolloClient<object>;
  showErnie: ShowErnie;
  analytics: UseLoyaltyEnrollmentAnalyticsReturn;
};

type SpringbigLoyaltyProps = {
  status: Statuses;
  setStatus: (status: Statuses) => void;
  deps: Dependencies;
};

export const createSpringbigLoyalty = ({ status, setStatus, deps }: SpringbigLoyaltyProps): UseLoyaltyReturn => {
  const { dispensary, cart, user: User, apolloClient, showErnie, analytics } = deps;
  const programName =
    dispensary.storeSettings.rewardsIntegrationConfiguration?.rewardsProgramDisplayName ??
    getDefaultRewardsProgramDisplayName({ location: dispensary.location ?? {} });
  const programDescription =
    dispensary.storeSettings.rewardsIntegrationConfiguration?.rewardsProgramDisplayDescription ??
    getDefaultRewardsProgramDisplayDescription({ location: dispensary.location ?? {} });

  const createUserAndPin = async (): Promise<void> => {
    setStatus(Statuses.loading);
    analytics.trackSubmitted();

    try {
      const { data: createdUserData } = await apolloClient.mutate({
        mutation: CreateSpringbigUserDocument,
        variables: {
          dispensaryId: dispensary.id,
          userId: User.id,
        },
      });

      if (!createdUserData) {
        analytics.trackFailed();
        showErnie('Error creating account, please try again.', ERNIE_TYPES.DANGER);
        setStatus(Statuses.terms);
        return;
      }

      const fetchStatus = await fetchWallet(cart, User, dispensary.id, apolloClient);

      if (fetchStatus === Statuses.signup) {
        analytics.trackFailed();
        showErnie('Error creating account, please try again.', ERNIE_TYPES.DANGER);
      } else {
        analytics.trackSucceeded();
      }

      setStatus(fetchStatus);
    } catch (error) {
      analytics.trackFailed();

      logger.error('Springbig loyalty signup failed', {
        dispensaryId: dispensary.id,
        integration: 'springbig',
        error: error instanceof Error ? error.message : String(error),
      });

      showErnie('Error creating account, please try again.', ERNIE_TYPES.DANGER);
      setStatus(Statuses.terms);
    }
  };

  return {
    createUserAndPin,
    status,
    setStatus,
    programName,
    programDescription,

    processPin: async (_pin) => Promise.resolve(),
    resendPin: async () => Promise.resolve(),
  };
};

const fetchWallet = async (
  cart: ReturnType<typeof Cart>,
  user: User,
  dispensaryId: string,
  apolloClient: ApolloClient<object>
): Promise<Statuses> => {
  const { loading, data } = await apolloClient.query({
    query: GetRewardsV2Document,
    variables: {
      dispensaryId,
      phoneNumber: user.phone,
      dob: formatBirthdayForRewards(user.birthday),
      pin: '',
      customerId: user.id,
    },
  });

  if (loading) {
    return Statuses.loading;
  }

  if (!data?.getRewardsV2) {
    return Statuses.signup;
  }

  setLoyaltyData({
    user,
    cart,
    data,
    dispensaryId,
  });

  return Statuses.success;
};
